import { DateTime } from 'luxon'
import { Query } from '@skedulo/protobuf-web'
import { getAppPreferences } from '../../core/globals/callbacks'
import { classifyScalar } from './classify-scalar'
import {
  evaluateEndpoint,
  extractRangeParts,
  parseDateValue,
  parseRangeOnly,
} from './date-parser'
import { looksLikeInSyntax, parseInSyntax } from './in-operator-parser'
import { Parser } from './parser'
import { AstNode, ComparisonNode } from './types'
import type {
  RequestFilter,
  RequestFilterComparison,
  RequestFilterCondition,
  RequestFilterGroup,
  LogicalOperator,
} from '../../types'

// Lightweight Lucene-like tokenizer and parser for a pragmatic subset used by ViewState filters

function operatorToComparison(
  operator: ComparisonNode['op'],
  scalar: string | string[],
): RequestFilterComparison {
  if (Array.isArray(scalar)) return 'IN'
  if (operator === ':') {
    if (scalar.startsWith('*') && scalar.endsWith('*')) return 'CONTAINS'
    if (!scalar.startsWith('*') && scalar.endsWith('*')) return 'STARTS_WITH'
    return 'EQUAL'
  }

  const tokenType = Parser.OPERATOR_SYMBOL_TO_TOKEN[operator]

  if (tokenType) {
    // Token type names match RequestFilterComparison values for non-colon operators
    return tokenType as RequestFilterComparison
  }

  // Should never happen for valid parsed operators
  throw new Error(`Unknown comparison operator: ${operator}`)
}

function astToGroup(
  ast: AstNode,
  timezone?: string,
  nowUtc?: string,
  schema?: FieldSchema,
): RequestFilterGroup {
  // normalize trees by operator
  const normalize = (node: AstNode, wantOp: LogicalOperator): AstNode[] => {
    if (node.kind === 'BINARY' && node.op === wantOp) {
      return [...normalize(node.left, wantOp), ...normalize(node.right, wantOp)]
    }
    return [node]
  }

  const build = (node: AstNode): RequestFilterGroup => {
    switch (node.kind) {
      case 'BINARY': {
        const operator = node.op
        const parts = normalize(node, operator)

        const conditions: RequestFilterCondition[] = parts.flatMap(
          (partNode) => {
            if (partNode.kind === 'COMPARISON') {
              return comparisonToConditions(
                partNode,
                false,
                timezone,
                nowUtc,
                schema,
              )
            }
            if (
              partNode.kind === 'NOT' &&
              partNode.node.kind === 'COMPARISON'
            ) {
              return comparisonToConditions(
                partNode.node,
                true,
                timezone,
                nowUtc,
                schema,
              )
            }
            // nested group
            return [
              {
                key: '',
                value: '',
                comparison: 'COMPARISON_UNSPECIFIED',
                not: partNode.kind === 'NOT',
                group: build(partNode),
              },
            ]
          },
        )

        return { operator, conditions }
      }

      case 'NOT': {
        const { node: innerNode } = node

        if (innerNode.kind === 'COMPARISON') {
          return {
            operator: 'AND',
            conditions: comparisonToConditions(
              innerNode,
              true,
              timezone,
              nowUtc,
              schema,
            ),
          }
        }

        return {
          operator: 'AND',
          conditions: [
            {
              key: '',
              value: '',
              comparison: 'COMPARISON_UNSPECIFIED',
              not: true,
              group: build(innerNode),
            },
          ],
        }
      }

      case 'GROUP':
        return build(node.node)

      default:
        // single comparison
        return {
          operator: 'AND',
          conditions: comparisonToConditions(
            node,
            false,
            timezone,
            nowUtc,
            schema,
          ),
        }
    }
  }

  return build(ast)
}

// Hack: This is to fix phone number EQUALS filtering with a leading 0 to not strip it out
// The ideal fix would be to check the data schema for the numeric type, but that would be much more difficult
const coercePreservingLeadingZero = (raw: string) => {
  // Only treat as string if it is composed solely of digits and starts with '0'
  if (/^0\d+$/.test(raw)) return raw
  return classifyScalar(raw).value
}

function isStringLikeKind(kind: Query.API.DataField_Kind | undefined): boolean {
  return (
    kind === Query.API.DataField_Kind.STRING ||
    kind === Query.API.DataField_Kind.ENUM
  )
}

function handleListNode(
  node: ComparisonNode,
  not: boolean,
  schema?: FieldSchema,
): RequestFilterCondition[] {
  if (node.value.kind !== 'LIST') return []

  const preserveStrings = isStringLikeKind(schema?.[node.key])
  return [
    {
      key: node.key,
      value: node.value.values.map((v) =>
        preserveStrings ? v : coercePreservingLeadingZero(v),
      ),
      comparison: 'IN',
      not,
    },
  ]
}

function handleInSyntaxNode(
  node: ComparisonNode,
  not: boolean,
  schema?: FieldSchema,
): RequestFilterCondition[] {
  if (
    node.op !== ':' ||
    !('value' in node.value) ||
    !looksLikeInSyntax(node.value.value)
  )
    return []
  const preserveStrings = isStringLikeKind(schema?.[node.key])
  const list = parseInSyntax(node.value.value, preserveStrings)
  return [{ key: node.key, value: list, comparison: 'IN', not }]
}

function buildRangeConditions<T>(
  node: ComparisonNode,
  not: boolean,
  parse: (value: string) => {
    lower: T | null
    upper: T | null
    lowerInclusive?: boolean
    upperInclusive?: boolean
  } | null,
): RequestFilterCondition[] {
  if (node.op !== ':' || !('value' in node.value)) return []

  const range = parse(node.value.value)
  if (!range) return []

  const conditions: RequestFilterCondition[] = []

  if (range.lower) {
    conditions.push({
      key: node.key,
      value: range.lower,
      comparison: range.lowerInclusive ? 'MORE_THAN_EQUAL' : 'MORE_THAN',
      not,
    })
  }

  if (range.upper) {
    conditions.push({
      key: node.key,
      value: range.upper,
      comparison: range.upperInclusive ? 'LESS_THAN_EQUAL' : 'LESS_THAN',
      not,
    })
  }

  return conditions
}

function handleDateRangeNode(
  node: ComparisonNode,
  not: boolean,
  timezone?: string,
  nowUtc?: string,
  schema?: FieldSchema,
) {
  const kind = schema?.[node.key]
  const schemaType =
    kind === Query.API.DataField_Kind.DATE
      ? 'DATE'
      : kind === Query.API.DataField_Kind.DATETIME
        ? 'DATETIME'
        : undefined

  return buildRangeConditions<string>(node, not, (value) => {
    // If we don't know the type from schema, we try to infer it from the range endpoints
    const parts = parseRangeOnly(value)
    const isExplicitDate = (val: string) => /:DATE\b/.test(val)
    const isExplicitDateTime = (val: string) => /:DATETIME\b/.test(val)
    const isDateOnlyLiteral = (val: string) => /^\d{4}-\d{2}-\d{2}$/.test(val)

    const getType = (val: string) => {
      if (isExplicitDate(val)) return 'DATE'
      if (isExplicitDateTime(val)) return 'DATETIME'
      if (schemaType) return schemaType
      if (isDateOnlyLiteral(val)) return 'DATE'
      return 'DATETIME'
    }

    // We take the type from the first non-star endpoint
    const effectiveType =
      parts.lower !== '*' ? getType(parts.lower) : getType(parts.upper)

    return parseDateValue(
      value,
      timezone || 'UTC',
      nowUtc,
      effectiveType as 'DATE' | 'DATETIME',
    )
  })
}

function handleNumericRangeNode(node: ComparisonNode, not: boolean) {
  return buildRangeConditions<number | undefined>(node, not, parseNumericRange)
}

function stripLuceneWildcards(
  scalar: unknown,
  operator: string,
  comparison: string,
) {
  if (operator !== ':' || typeof scalar !== 'string') {
    return scalar
  }

  if (comparison === 'STARTS_WITH') {
    // 'Foo*' → 'Foo'
    return scalar.endsWith('*') ? scalar.slice(0, -1) : scalar
  }

  if (comparison === 'CONTAINS') {
    // '*Foo*' → 'Foo' (leave lone '*' untouched)
    if (scalar === '*') return scalar

    const withoutLeading = scalar.startsWith('*') ? scalar.slice(1) : scalar

    return withoutLeading.endsWith('*')
      ? withoutLeading.slice(0, -1)
      : withoutLeading
  }

  return scalar
}

function handleDefaultNode(
  node: ComparisonNode,
  not: boolean,
  timezone?: string,
  nowUtc?: string,
  schema?: FieldSchema,
): RequestFilterCondition[] {
  if (!('value' in node.value)) return []

  const rawValue = node.value.value
  const kind = schema?.[node.key]

  const isDate = kind === Query.API.DataField_Kind.DATE
  const isDateTime = kind === Query.API.DataField_Kind.DATETIME

  const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/

  function resolveEndpointType(
    raw: string,
    isDate: boolean,
    isDateTime: boolean,
  ): 'DATE' | 'DATETIME' {
    if (/:DATE\b/.test(raw)) return 'DATE'
    if (/:DATETIME\b/.test(raw)) return 'DATETIME'
    if (isDate) return 'DATE'
    if (isDateTime) return 'DATETIME'
    return DATE_ONLY_RE.test(raw) ? 'DATE' : 'DATETIME'
  }

  // If it's a date field or looks like a date/now-math, evaluate it
  const isNowMath = rawValue.startsWith('NOW')
  const hasExplicitCast = /:(?:DATE|DATETIME)\b/.test(rawValue)

  const shouldEvaluateAsDate =
    isDate || isDateTime || isNowMath || hasExplicitCast

  if (shouldEvaluateAsDate) {
    try {
      const type = resolveEndpointType(rawValue, isDate, isDateTime)
      const effectiveTimezone = timezone ?? 'UTC'
      const effectiveNowUtc = nowUtc ?? DateTime.utc().toISO()

      const value = evaluateEndpoint(
        rawValue,
        'lower',
        effectiveTimezone,
        effectiveNowUtc,
        type,
      )

      return [
        {
          key: node.key,
          value,
          comparison: operatorToComparison(node.op, value),
          not,
        },
      ]
    } catch {
      // Fallback to default if evaluation fails (e.g. not actually a date)
    }
  }

  const scalar = coercePreservingLeadingZero(rawValue)
  const comparison = operatorToComparison(
    node.op,
    typeof scalar === 'string' ? scalar : String(scalar),
  )
  const value = stripLuceneWildcards(scalar, node.op, comparison)

  return [{ key: node.key, value, comparison, not }]
}

function comparisonToConditions(
  node: ComparisonNode,
  not: boolean,
  timezone?: string,
  nowUtc?: string,
  schema?: FieldSchema,
): RequestFilterCondition[] {
  const listNodes = handleListNode(node, not, schema).concat(
    handleInSyntaxNode(node, not, schema),
  )
  if (listNodes.length > 0) return listNodes

  if ('value' in node.value && looksLikeRange(node.value.value)) {
    const rangeNodes = looksLikeDateRange(node.value.value)
      ? handleDateRangeNode(node, not, timezone, nowUtc, schema)
      : handleNumericRangeNode(node, not)

    if (rangeNodes.length > 0) return rangeNodes
  }

  return handleDefaultNode(node, not, timezone, nowUtc, schema)
}

function looksLikeRange(raw: string): boolean {
  return extractRangeParts(raw) !== null
}

function looksLikeDateRange(raw: string): boolean {
  const looksLikeAnyRange = looksLikeRange(raw)

  if (!looksLikeAnyRange) return false

  const range = parseRangeOnly(raw)

  const isStar = (t: string) => t === '*'
  const isNowMacro = (t: string) => t.startsWith('NOW')
  const isValidDateTime = (t: string) =>
    DateTime.fromISO(t, { setZone: true }).isValid

  const isDateLike = (t: string) =>
    !isStar(t) && (isNowMacro(t) || isValidDateTime(t))

  // Consider it a date range only if at least one endpoint is date-like.
  // Purely numeric endpoints should be treated as numeric ranges elsewhere.
  return isDateLike(range.lower) || isDateLike(range.upper)
}

// Parse a Lucene-style range as a numeric/duration range
function parseNumericRange(raw: string): {
  lower: number | null
  upper: number | null
  lowerInclusive: boolean
  upperInclusive: boolean
} | null {
  const string = raw.trim()
  if (!string) return null

  const parts = parseRangeOnly(string)

  const toNumber = (value: string): number | null => {
    if (value === '*') return null
    const numeric = Number.parseFloat(value)
    return Number.isNaN(numeric) ? null : numeric
  }

  const lower = toNumber(parts.lower)
  const upper = toNumber(parts.upper)

  // If any non-* endpoint is not numeric, this isn't a numeric range
  if (
    (parts.lower !== '*' && lower === null) ||
    (parts.upper !== '*' && upper === null)
  ) {
    return null
  }

  return {
    lower,
    upper,
    lowerInclusive: parts.lowerInclusive,
    upperInclusive: parts.upperInclusive,
  }
}

export const BROWSER_TIMEZONE =
  Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'Etc/UTC'

export const getDefaultTimezone = () => {
  const appPreferences = getAppPreferences()
  return appPreferences?.defaultRegion?.Timezone ?? BROWSER_TIMEZONE
}

export type FieldSchema = Record<string, Query.API.DataField_Kind>

/**
 * Parses a Lucene filter query string into a request filter object.
 * @param input - Lucene filter query string, e.g. `filter_Name:Foo* OR filter_Code:$in[a,b,c]`
 * @param timezone - Timezone used to convert Lucene relative date range strings.  Any other existing date times are retained, assuming that the FE converts them to UTC.
 * @param nowUtc - Optional parameter to override the current UTC time.  Only used for testing.
 * @param schema - Optional schema mapping field names to their data types.
 */
export function parseQueryToRequestFilter(
  input: string | null | undefined,
  timezone: string = getDefaultTimezone(),
  nowUtc: string = DateTime.utc().toISO(),
  schema?: FieldSchema,
): RequestFilter {
  const trimmed = input?.trim() ?? ''
  if (!trimmed || trimmed === '') {
    return { group: { operator: 'AND', conditions: [] } }
  }

  const parser = new Parser(trimmed)
  const ast = parser.parse()
  const group = astToGroup(ast, timezone, nowUtc, schema)
  return { group }
}
